import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, TextField, Grid, CircularProgress,
  Alert, Chip, Divider, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Card, CardContent, CardActionArea, MenuItem, Select,
  Checkbox, FormControlLabel,
  FormControl, InputLabel, Table, TableHead, TableBody, TableRow, TableCell,
  Autocomplete, Stepper, Step, StepLabel, Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Description as DocIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { bevestig } from '../../utils/bevestig';
import { crewUitTitel } from '../../utils/draaidagCrew';
import { fullName, matchCrewPersoon, externGemarkeerd, isExternGemarkeerd } from '../../utils/crewMatch';

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('nl-NL', { 
      day: 'numeric', month: 'short', year: 'numeric' 
    });
  } catch { return dateStr; }
};

// Naam → persoon matchen (voor crew-resolutie): zie utils/crewMatch (medewerker-voorrang bij gelijke voornaam).
const _fullName = fullName;
const _functie = (p) => { const f = p?.fields?.Functie; return Array.isArray(f) ? (f[0] || '') : (f || ''); };
const _telefoon = (p) => { const t = p?.fields?.Telefoonnummer; return Array.isArray(t) ? t[0] : (t || ''); };

// Crew-namen uit de draaidag-naam halen. Conventie: "Naam (b) + Naam (b) + … || Klant - Project - [nr] - PL".
// We pakken het deel vóór de '||' en splitsen op '+', zonder de '(b)'-achtige suffixes.
function parseCrewFromDraaidagName(name) {
  if (!name) return [];
  const before = String(name).split('||')[0];
  return before.split('+')
    .map(s => s.replace(/\([^)]*\)/g, '').trim())          // strip "(b)" e.d.
    .map(s => s.replace(/^\s*opt(ie)?\s*:?\s*/i, '').trim()) // strip "OPTIE:"-prefix (voorlopige boeking)
    .filter(s => s && s.length > 1 && !/^\d/.test(s) && !/[[\]]/.test(s));
}

// Create Callsheet Dialog with Autocomplete for draaidagen search
function CreateCallsheetDialog({ open, onClose, onCreated, draaidagen, initialDraaidag = null }) {
  const [selectedDraaidag, setSelectedDraaidag] = useState(null);

  // Bij openen vanuit een project met één draaidag: die meteen voorselecteren.
  useEffect(() => {
    if (open && initialDraaidag && !selectedDraaidag) {
      setSelectedDraaidag(initialDraaidag);
    }
  }, [open, initialDraaidag]); // eslint-disable-line react-hooks/exhaustive-deps
  const [titel, setTitel] = useState('');
  const [datum, setDatum] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Ruwe callsheet: crew + deliverables overnemen uit de draaidag-agenda.
  const [autofill, setAutofill] = useState(true);
  const [aiNaAanmaken, setAiNaAanmaken] = useState(true); // meteen 'Voorbereiden met AI' in de editor (Joris 18 sep)
  const [includeCrew, setIncludeCrew] = useState(true);
  const [includeDeliverables, setIncludeDeliverables] = useState(false); // standaard uit — wordt zelden gebruikt
  const [personPool, setPersonPool] = useState([]);
  const [crewResolution, setCrewResolution] = useState([]); // [{ name, person, reden, checked }]
  const [step, setStep] = useState(0);                       // 0 = basis, 1 = voorvullen
  const [crewResult, setCrewResult] = useState(null);        // crew-diagnostiek na aanmaken
  const [deliverables, setDeliverables] = useState([]);      // [{ id, line, checked }]
  const [delivLoading, setDelivLoading] = useState(false);

  // Deliverables van het project ophalen zodra een draaidag is gekozen (voor selectie).
  useEffect(() => {
    if (!selectedDraaidag || !autofill || !includeDeliverables) { setDeliverables([]); return; }
    let cancelled = false;
    setDelivLoading(true);
    authFetch(`${BACKEND_URL}/Api/callsheets/draaidag-deliverables/${selectedDraaidag.id}`)
      .then(r => leesJson(r, 'Deliverables laden'))
      .then(d => { if (!cancelled) setDeliverables((d.deliverables || []).map(x => ({ ...x, checked: true }))); })
      .catch((e) => { if (!cancelled) { meldFout(e, 'Deliverables laden'); setDeliverables([]); } })
      .finally(() => { if (!cancelled) setDelivLoading(false); });
    return () => { cancelled = true; };
  }, [selectedDraaidag, autofill, includeDeliverables]);

  // Personen-pool ophalen (medewerkers + Leveranciers-freelancers) zodra het dialoog opent.
  useEffect(() => {
    if (!open) return;
    authFetch(`${BACKEND_URL}/Api/personen/crew`)
      .then(r => leesJson(r, 'Personen laden')).then(d => setPersonPool(d.records || [])).catch((e) => { meldFout(e, 'Personen laden'); setPersonPool([]); });
  }, [open]);

  // Crew uit de draaidag → voorgevinkte resolutie (naam gematcht aan een persoon).
  useEffect(() => {
    if (!selectedDraaidag || !autofill || !includeCrew) { setCrewResolution([]); return; }
    // Crew uit het gekoppelde veld ÉN uit de draaidag-naam (de "+"-lijst).
    const linked = selectedDraaidag.fields?.['Naam (from Crew)'] || [];
    const fromName = parseCrewFromDraaidagName(selectedDraaidag.fields?.Draaidag);
    // (b)/(g) in de titel = extern → geen medewerker-voorrang bij gelijke voornaam.
    const extern = externGemarkeerd(crewUitTitel(selectedDraaidag.fields?.Draaidag));
    // Match elke naam aan een persoon en ONTDUBBEL op de gekoppelde persoon (zodat
    // "Pepijn" + "OPTIE: Pepijn" niet twee keer als Pepijn Robben verschijnen).
    // Niet-herkende namen ontdubbelen we op de naam zelf.
    const rows = [];
    const seenPerson = new Set();
    const seenName = new Set();
    for (const name of [...linked, ...fromName]) {
      const clean = (name || '').trim();
      if (!clean) continue;
      const match = matchCrewPersoon(clean, personPool, { extern: isExternGemarkeerd(clean, extern) });
      const person = match?.person || null;
      if (person) {
        if (seenPerson.has(person.id)) continue;
        seenPerson.add(person.id);
      } else {
        const k = clean.toLowerCase();
        if (seenName.has(k)) continue;
        seenName.add(k);
      }
      rows.push({ name: clean, person, reden: match?.reden || null, checked: true });
    }
    setCrewResolution(rows);
  }, [selectedDraaidag, autofill, includeCrew, personPool]);

  const updateCrewRow = (i, patch) => setCrewResolution(prev => prev.map((c, idx) => idx === i ? { ...c, ...patch } : c));

  const handleCreate = async () => {
    if (!selectedDraaidag) {
      setError('Selecteer een draaidag');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = autofill ? '/Api/callsheets/from-draaidag' : '/Api/callsheets';
      // Door de PL bevestigde/gekoppelde crew (alleen aangevinkte). Leveranciers-base
      // personen (_source) zijn niet koppelbaar → naam-only.
      const crew = (autofill && includeCrew && crewResolution.length)
        ? crewResolution.filter(c => c.checked).map(c => ({
            name: c.person ? _fullName(c.person) : c.name,
            personenId: (c.person && !c.person._source) ? c.person.id : undefined,
            functie: c.person ? _functie(c.person) : undefined,
            telefoon: c.person ? _telefoon(c.person) : undefined,
          }))
        : undefined;
      // Geselecteerde deliverable-regels (alleen aangevinkte).
      const deliverableLines = (autofill && includeDeliverables && deliverables.length)
        ? deliverables.filter(d => d.checked).map(d => d.line)
        : undefined;
      const response = await authFetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draaidagId: selectedDraaidag.id,
          titel: titel || undefined,
          datum: datum || undefined,
          ...(autofill ? { includeCrew, includeDeliverables, ...(crew ? { crew } : {}), ...(deliverableLines ? { deliverableLines } : {}) } : {}),
        })
      });
      
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Aanmaken mislukt');
      }

      // Crew-diagnostiek: als er crew niet is aangemaakt, of functie/link is weggevallen,
      // toon dat eerst (zodat we zien wat er bij bv. Buckaroo misgaat) i.p.v. stil sluiten.
      const diag = result.crewDiagnostics || [];
      const problems = diag.filter((d) => !d.created || d.droppedFunctie || d.droppedLink);
      if (problems.length) {
        setCrewResult({ callsheet: result.callsheet, diag, crewCreated: result.crewCreated, crewInput: result.crewInput, crewSource: result.crewSource });
      } else {
        onCreated({ ...result.callsheet, aiNaAanmaken });
        handleClose();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    setSelectedDraaidag(null);
    setTitel('');
    setDatum('');
    setError(null);
    setStep(0);
    setDeliverables([]);
    setCrewResolution([]);
    onClose();
  };
  
  // Pure-datum (YYYY-MM-DD) uit een draaidag halen. Voorkeur: StartDatum (pure date,
  // geen tijdzone-schuif); val terug op StartdatumOutlook (UTC → eerste 10 tekens).
  const pickDraaidagDate = (fields) => {
    const pure = fields?.StartDatum || fields?.StartDatumOutlook;
    if (pure && /^\d{4}-\d{2}-\d{2}/.test(String(pure))) return String(pure).slice(0, 10);
    const out = fields?.StartdatumOutlook;
    return out ? String(out).split('T')[0] : '';
  };

  // Auto-fill data when draaidag is selected — datum LIVE uit Airtable (mirror loopt achter).
  useEffect(() => {
    if (!selectedDraaidag) return;
    const f = selectedDraaidag.fields || {};
    const fromMirror = pickDraaidagDate(f);
    if (fromMirror) setDatum(fromMirror);
    const klant = f['KlantnaamString (from Project)']?.[0];
    const projectnaam = f['Projectnaam (from Project)']?.[0];
    const autoTitle = [klant, projectnaam].filter(Boolean).join(' - ') || f.Draaidag || '';
    if (autoTitle) setTitel(autoTitle);

    // Verse datum rechtstreeks uit Airtable (overschrijft de mogelijk verouderde mirror).
    let cancelled = false;
    authFetch(`${BACKEND_URL}/Api/draaidagen/${selectedDraaidag.id}/live`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d?.fields) { const fresh = pickDraaidagDate(d.fields); if (fresh) setDatum(fresh); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedDraaidag]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Get display label for draaidag (pure datum → geen tijdzone-schuif in het overzicht)
  const getDraaidagLabel = (dd) => {
    if (!dd) return '';
    const name = dd.fields?.Draaidag || dd.id;
    const pure = pickDraaidagDate(dd.fields);
    const date = pure ? formatDate(pure) : '';
    return date ? `${name} (${date})` : name;
  };
  
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DocIcon sx={{ color: '#3b82f6' }} />
          Callsheet aanmaken
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Crew-diagnostiek: wat is wel/niet aangemaakt en waarom */}
        {crewResult && (
          <Box>
            <Alert severity={crewResult.crewCreated < crewResult.crewInput ? 'warning' : 'info'} sx={{ mb: 2 }}>
              <b>{crewResult.crewCreated} van {crewResult.crewInput} crew aangemaakt</b> (bron: {crewResult.crewSource}).
              {crewResult.crewCreated < crewResult.crewInput ? ' Sommige crew-rijen zijn niet in Airtable gekomen — zie hieronder.' : ' Enkele velden vielen weg (functie/koppeling).'}
            </Alert>
            <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 1 }}>
              {(crewResult.diag || []).map((d, i) => (
                <Box key={i} sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{d.created ? '✅' : '❌'}</span>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{d.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.created
                        ? `aangemaakt (${d.via})${d.droppedFunctie ? ' — functie viel weg' : ''}${d.droppedLink ? ' — personen-koppeling viel weg' : ''}`
                        : `NIET aangemaakt — ${d.error}`}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {!crewResult && (<>


        <Stepper activeStep={step} sx={{ mb: 3 }}>
          <Step><StepLabel>Basis</StepLabel></Step>
          <Step><StepLabel>Voorvullen</StepLabel></Step>
        </Stepper>

        {/* ── Stap 1: basis ── */}
        {step === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Kies de draaidag; titel en datum worden automatisch ingevuld (klant + project).
            </Typography>
            <Autocomplete
              value={selectedDraaidag}
              onChange={(_, newValue) => setSelectedDraaidag(newValue)}
              options={draaidagen || []}
              getOptionLabel={getDraaidagLabel}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              filterOptions={(options, { inputValue }) => {
                const searchLower = inputValue.toLowerCase();
                return options.filter(dd => {
                  const name = (dd.fields?.Draaidag || '').toLowerCase();
                  const date = (dd.fields?.StartdatumOutlook || '').toLowerCase();
                  const week = (dd.fields?.Week || '').toLowerCase();
                  return name.includes(searchLower) || date.includes(searchLower) || week.includes(searchLower);
                });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Draaidag *" placeholder="Zoek op naam of datum..." sx={{ mb: 2 }} />
              )}
              renderOption={(props, dd) => (
                <li {...props} key={dd.id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{dd.fields?.Draaidag || dd.id}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(pickDraaidagDate(dd.fields))} {dd.fields?.Week && `• ${dd.fields.Week}`}
                    </Typography>
                  </Box>
                </li>
              )}
              noOptionsText="Geen draaidagen gevonden"
              fullWidth
            />
            <TextField fullWidth label="Datum" type="date" value={datum}
              onChange={(e) => setDatum(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
            <TextField fullWidth label="Titel callsheet (klant + project)"
              placeholder="Bijv. Brandwebbing B.V. - Previder commercials" value={titel}
              onChange={(e) => setTitel(e.target.value)} multiline rows={2} required />
          </Box>
        )}

        {/* ── Stap 2: voorvullen (crew + deliverables) ── */}
        {step === 1 && (
          <Box>
            <FormControlLabel
              control={<Checkbox checked={autofill} onChange={(e) => setAutofill(e.target.checked)} size="small" />}
              label={<Typography variant="body2" fontWeight={600}>Voorvullen uit de draaidag-agenda</Typography>}
            />
            <FormControlLabel sx={{ display: 'flex', mt: 0.5 }}
              control={<Checkbox checked={aiNaAanmaken} onChange={(e) => setAiNaAanmaken(e.target.checked)} size="small" />}
              label={<Box><Typography variant="body2" fontWeight={600}>✨ Daarna voorbereiden met AI</Typography><Typography variant="caption" color="text.secondary">Leest notities, meetings, mails en het dossier van het project en stelt opmerkingen, productievelden en een crew-briefing voor. Je kunt daar ook nog mails of tekst bij plakken.</Typography></Box>} />
            {!autofill && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4 }}>
                Lege callsheet — crew, locaties en planning vul je later in de callsheet zelf aan.
              </Typography>
            )}

            {autofill && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                {/* Crew */}
                <Box sx={{ p: 1.5, borderRadius: 1, border: '1px solid #e5e7eb' }}>
                  <FormControlLabel
                    control={<Checkbox checked={includeCrew} onChange={(e) => setIncludeCrew(e.target.checked)} size="small" />}
                    label={<Typography variant="body2" fontWeight={600}>Crew overnemen</Typography>}
                  />
                  {includeCrew && (crewResolution.length > 0 ? (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Vink aan wie mee moet; koppel/wijzig de persoon waar nodig (functie + telefoon volgen automatisch).
                      </Typography>
                      {crewResolution.map((c, i) => (
                        <Box key={`${c.name}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                          <Checkbox checked={c.checked} size="small" sx={{ p: 0.25 }}
                            onChange={(e) => updateCrewRow(i, { checked: e.target.checked })} />
                          <Typography variant="caption" sx={{ width: 90, flexShrink: 0, color: c.person ? 'text.secondary' : 'warning.main' }}>{c.name}</Typography>
                          <Autocomplete
                            size="small" sx={{ flex: 1 }} options={personPool} value={c.person}
                            getOptionLabel={(p) => _fullName(p)} isOptionEqualToValue={(o, v) => o.id === v.id}
                            onChange={(_e, val) => updateCrewRow(i, { person: val, reden: null })}
                            renderInput={(params) => (
                              <TextField {...params} variant="standard"
                                placeholder={c.person ? '' : 'Koppel persoon…'}
                                helperText={!c.person ? 'Niet herkend — kies handmatig' : [c.person._source === 'leverancier' ? 'Freelancer' : _functie(c.person), c.reden].filter(Boolean).join(' · ')} />
                            )}
                          />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4 }}>Geen crew gevonden in deze draaidag.</Typography>
                  ))}
                </Box>

                {/* Deliverables */}
                <Box sx={{ p: 1.5, borderRadius: 1, border: '1px solid #e5e7eb' }}>
                  <FormControlLabel
                    control={<Checkbox checked={includeDeliverables} onChange={(e) => setIncludeDeliverables(e.target.checked)} size="small" />}
                    label={<Typography variant="body2" fontWeight={600}>Deliverables overnemen <Typography component="span" variant="caption" color="warning.main">(⚠️ controleer t.o.v. offerte)</Typography></Typography>}
                  />
                  {includeDeliverables && (delivLoading ? (
                    <CircularProgress size={16} sx={{ ml: 4 }} />
                  ) : deliverables.length > 0 ? (
                    <Box sx={{ mt: 0.5 }}>
                      {deliverables.map((d, i) => (
                        <Box key={d.id || i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox checked={d.checked} size="small" sx={{ p: 0.25 }}
                            onChange={(e) => setDeliverables(prev => prev.map((x, idx) => idx === i ? { ...x, checked: e.target.checked } : x))} />
                          <Typography variant="caption">{d.line}</Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4 }}>Geen deliverables gevonden voor dit project.</Typography>
                  ))}
                </Box>
              </Stack>
            )}
          </Box>
        )}
        </>)}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {crewResult ? (
          <>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" onClick={() => { const cs = crewResult.callsheet; setCrewResult(null); onCreated({ ...cs, aiNaAanmaken }); handleClose(); }}>
              Callsheet openen
            </Button>
          </>
        ) : (<>
          <Button onClick={handleClose} disabled={loading}>Annuleren</Button>
          <Box sx={{ flex: 1 }} />
          {step === 1 && <Button onClick={() => setStep(0)} disabled={loading}>Vorige</Button>}
          {step === 0 && (
            <Button variant="contained" disabled={!selectedDraaidag || !titel} onClick={() => setStep(1)}>Volgende</Button>
          )}
          {step === 1 && (
            <Button variant="contained" onClick={handleCreate} disabled={loading || !selectedDraaidag || !titel}>
              {loading ? <CircularProgress size={20} /> : 'Aanmaken'}
            </Button>
          )}
        </>)}
      </DialogActions>
    </Dialog>
  );
}

// Main CallSheet List Component
function CallSheetList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');
  const wantNew = searchParams.get('new');   // vanuit een project: open direct het maak-dialoog
  // Vanuit een draaidag-taak (projectpagina, 15 sep): draaidag op id of op naam voorselecteren.
  const wantDraaidagId = searchParams.get('draaidag');
  const wantNaam = (searchParams.get('naam') || '').trim().toLowerCase();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [callsheets, setCallsheets] = useState([]);
  const [draaidagen, setDraaidagen] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);
  // Dupliceren: alles kopiëren (crew/cast/programma/locaties), alleen een
  // andere datum kiezen — daarna gewoon bewerken.
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [duplicateDatum, setDuplicateDatum] = useState('');
  const [duplicateDraaidag, setDuplicateDraaidag] = useState(null); // gekozen draaidag voor de kopie (optioneel)
  const [duplicating, setDuplicating] = useState(null);

  const doDuplicate = async () => {
    if (!duplicateTarget) return;
    const cs = duplicateTarget;
    setDuplicating(cs.id);
    setDuplicateTarget(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${cs.id}/duplicate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ datum: duplicateDatum || null, draaidagId: duplicateDraaidag?.id || null }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      navigate(`/callsheets/${d.id}`);
    } catch (e) {
      setError(`Dupliceren mislukt: ${e.message}`);
    } finally {
      setDuplicating(null);
      setDuplicateDatum('');
      setDuplicateDraaidag(null);
    }
  };

  // Draaidagen van hetzelfde project als de te dupliceren callsheet — de kopie hoort aan een draaidag te
  // hangen, anders vallen klantcontact, projectnummer en PL weg (tool-feedback #60/#61, 21 sep 2026).
  const duplicateProjectNr = String(duplicateTarget?.fields?.Projectnummer
    || duplicateTarget?.fields?.['Projectnr_en_naam (from Project) (from DraaidagenOutlook)']?.[0] || '').match(/\d{4,5}/)?.[0] || '';
  const duplicateKandidaten = useMemo(() => {
    if (!duplicateProjectNr) return [];
    return draaidagen
      .filter((d) => String(d.fields?.['Projectnummer (from Project)']?.[0] || '') === duplicateProjectNr)
      .sort((a, b) => String(a.fields?.StartDatum || '').localeCompare(String(b.fields?.StartDatum || '')));
  }, [draaidagen, duplicateProjectNr]);
  useEffect(() => {
    if (!duplicateDatum) { setDuplicateDraaidag(null); return; }
    const opDatum = duplicateKandidaten.filter((d) => String(d.fields?.StartDatum || '').slice(0, 10) === duplicateDatum);
    setDuplicateDraaidag(opDatum.length === 1 ? opDatum[0] : null);
  }, [duplicateDatum, duplicateKandidaten]);

  // Kom je vanuit een project met ?new=1 → open meteen het maak-dialoog (geen omweg via het overzicht).
  useEffect(() => {
    if (wantNew && !loading && !autoOpened) {
      setShowCreateDialog(true);
      setAutoOpened(true);
    }
  }, [wantNew, loading, autoOpened]);
  
  // Fetch callsheets
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Calculate startDate for draaidagen (7 days ago)
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      const startDateStr = startDate.toISOString().split('T')[0];

      // Fetch callsheets AND draaidagen in parallel
      const [callsheetResult, draaidagenResult] = await Promise.allSettled([
        authFetch(`${BACKEND_URL}/Api/callsheets`).then(async (res) => {
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.error || 'Callsheets ophalen mislukt');
          }
          return res.json();
        }),
        authFetch(`${BACKEND_URL}/Api/draaidagen?startDate=${startDateStr}`).then(async (res) => {
          if (!res.ok) return [];
          return res.json();
        })
      ]);

      // Process callsheets
      if (callsheetResult.status === 'fulfilled') {
        setCallsheets(callsheetResult.value.records || []);
      } else {
        console.error('Error fetching callsheets:', callsheetResult.reason);
        setError(callsheetResult.reason?.message || 'Callsheets ophalen mislukt');
      }

      // Process draaidagen
      if (draaidagenResult.status === 'fulfilled') {
        const draaidagenData = draaidagenResult.value;
        const draaidagenArray = Array.isArray(draaidagenData) ? draaidagenData : (draaidagenData.records || []);

        let filteredDraaidagen = draaidagenArray;

        // If project is specified, filter by project
        if (projectId) {
          filteredDraaidagen = filteredDraaidagen.filter(d => {
            const projectLinks = d.fields?.Project || d.fields?.project || [];
            return Array.isArray(projectLinks) && projectLinks.includes(projectId);
          });
        }

        // Sort by date
        filteredDraaidagen.sort((a, b) => {
          const dateA = new Date(a.fields?.StartdatumOutlook || a.fields?.StartDatumOutlook || 0);
          const dateB = new Date(b.fields?.StartdatumOutlook || b.fields?.StartDatumOutlook || 0);
          return dateA - dateB;
        });

        setDraaidagen(filteredDraaidagen.slice(0, 50));
      } else {
        console.error('Error fetching draaidagen:', draaidagenResult.reason);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleCallsheetCreated = (newCallsheet) => {
    // ?fresh=1 → de editor doet een stille her-fetch zodat async toegevoegde
    // standaard-planning (Airtable-automation) vanzelf verschijnt (geen refresh nodig).
    navigate(`/callsheets/${newCallsheet.id}?fresh=1${newCallsheet.aiNaAanmaken ? '&ai=1' : ''}`);
  };
  
  // Group callsheets by project
  // Vanuit een project: alleen de callsheets van de draaidagen van dát project.
  const projectDraaidagIds = new Set(draaidagen.map((d) => d.id));
  const zichtbareCallsheets = projectId
    ? callsheets.filter((cs) => (cs.fields?.Draaidag || []).some((did) => projectDraaidagIds.has(did)))
    : callsheets;
  const groupedCallsheets = callsheets.reduce((acc, cs) => {
    const projectNum = cs.fields?.['Projectnummer'] || cs.fields?.['Projectnr_en_naam (from Project) (from DraaidagenOutlook)']?.[0] || 'Overig';
    if (!acc[projectNum]) acc[projectNum] = [];
    acc[projectNum].push(cs);
    return acc;
  }, {});
  
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, border: '1px solid #e5e7eb' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DocIcon sx={{ fontSize: 32, color: '#f97316' }} />
            <Box>
              <Typography variant="h4" fontWeight={700}>
                📋 Callsheet aanmaken
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Maak callsheets aan voor draaidagen en genereer PDF's
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={fetchData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateDialog(true)}
              disabled={draaidagen.length === 0}
            >
              Nieuwe Callsheet
            </Button>
          </Box>
        </Box>
        
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        
        {/* Show info when filtering by project */}
        {projectId && !loading && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Toont {draaidagen.length} draaidagen voor dit project. 
            {draaidagen.length === 0 && ' Geen recente of toekomstige draaidagen gevonden.'}
          </Alert>
        )}
      </Paper>
      
      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {/* Callsheets Table */}
      {!loading && callsheets.length > 0 && (
        <Paper sx={{ border: '1px solid #e5e7eb' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell><strong>Titel</strong></TableCell>
                <TableCell><strong>Datum</strong></TableCell>
                <TableCell><strong>Project</strong></TableCell>
                <TableCell><strong>Status PDF</strong></TableCell>
                <TableCell><strong>Acties</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {zichtbareCallsheets.slice(0, 50).map((cs) => (
                <TableRow 
                  key={cs.id} 
                  hover
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f8fafc' } }}
                  onClick={() => navigate(`/callsheets/${cs.id}`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {cs.fields?.Titel || 'Geen titel'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {formatDate(cs.fields?.['Datum (handmatig)'] || cs.fields?.Datum)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {cs.fields?.['Projectnr_en_naam (from Project) (from DraaidagenOutlook)']?.[0] || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {cs.fields?.['PDF-bestand']?.[0]?.url ? (
                      <Chip 
                        icon={<PdfIcon />}
                        label="PDF beschikbaar"
                        size="small"
                        color="success"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(cs.fields['PDF-bestand'][0].url, '_blank');
                        }}
                      />
                    ) : (
                      <Chip 
                        label="Geen PDF"
                        size="small"
                        color="default"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/callsheets/${cs.id}`);
                        }}
                      >
                        Bekijk
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={duplicating === cs.id}
                        onClick={(e) => { e.stopPropagation(); setDuplicateTarget(cs); }}
                      >
                        {duplicating === cs.id ? 'Kopiëren…' : 'Dupliceren'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={async (e) => {
                          e.stopPropagation();
                          const ok = await bevestig({ titel: 'Callsheet verwijderen?', tekst: `“${cs.fields?.Titel || 'Callsheet'}” met crew, planning en locaties wordt verwijderd. Dit is niet ongedaan te maken.`, knop: 'Verwijderen', gevaarlijk: true });
                          if (!ok) return;
                          try {
                            const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${cs.id}`, { method: 'DELETE' });
                            if (!r.ok) throw new Error(`HTTP ${r.status}`);
                            setCallsheets((prev) => prev.filter((x) => x.id !== cs.id));
                          } catch (err) { setError(`Verwijderen mislukt: ${err.message}`); }
                        }}
                      >
                        Verwijder
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
      
      {/* Empty state */}
      {!loading && callsheets.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', border: '1px solid #e5e7eb' }}>
          <DocIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Geen callsheets gevonden
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Maak een nieuwe callsheet aan om te beginnen
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => setShowCreateDialog(true)}
          >
            Nieuwe Callsheet
          </Button>
        </Paper>
      )}
      
      {/* Dupliceer-dialoog: alles wordt gekopieerd, alleen de datum kies je */}
      <Dialog open={!!duplicateTarget} onClose={() => setDuplicateTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Callsheet dupliceren</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            "{duplicateTarget?.fields?.Titel || 'Callsheet'}" wordt volledig gekopieerd —
            crew, cast, programma, locaties en alle opmerkingen. Kies de datum van de
            nieuwe draaidag; daarna kun je alles gewoon aanpassen.
          </Typography>
          <TextField
            label="Datum nieuwe callsheet"
            type="date"
            fullWidth
            value={duplicateDatum}
            onChange={(e) => setDuplicateDatum(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          {duplicateKandidaten.length > 0 && (
            <Autocomplete
              size="small" sx={{ mt: 2 }}
              options={duplicateKandidaten}
              value={duplicateDraaidag}
              onChange={(_e, v) => setDuplicateDraaidag(v)}
              getOptionLabel={(d) => `${String(d.fields?.StartDatum || '').slice(0, 10)} · ${d.fields?.Draaidag || d.id}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="Koppel aan draaidag (zelfde project)" />}
            />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {duplicateDraaidag
              ? `Wordt gekoppeld aan de draaidag van ${String(duplicateDraaidag.fields?.StartDatum || '').slice(0, 10)}: klantcontact, projectnummer en PL komen daar vandaan.`
              : 'Geen draaidag gekozen: we zoeken zelf de draaidag van dit project op de gekozen datum. Is die er niet, dan nemen we klantcontact en PL uit het origineel over als handmatige velden.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateTarget(null)}>Annuleren</Button>
          <Button variant="contained" onClick={doDuplicate} disabled={!duplicateDatum}>
            Dupliceren
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <CreateCallsheetDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={handleCallsheetCreated}
        draaidagen={draaidagen}
        initialDraaidag={
          (wantDraaidagId && draaidagen.find((d) => d.id === wantDraaidagId))
          || (wantNaam && draaidagen.find((d) => String(d.fields?.Draaidag || d.fields?.Titel || '').trim().toLowerCase() === wantNaam))
          || (draaidagen.length === 1 ? draaidagen[0] : null)
        }
      />
    </Box>
  );
}

export default CallSheetList;
